import { useEffect, useRef, useState } from 'react';

import type { Health } from '@inbound/contract';

import { useGetHealthQuery } from '../store/health-api.js';
import styles from './ConnectionStatus.module.css';

/**
 * Where the pill gives up its label and becomes a dot. The same breakpoint at
 * which the header hides the theme switch (AppShell.module.css).
 */
const COMPACT_QUERY = '(max-width: 560px)';

/**
 * The header's connection indicator (S03: "a real connection-status indicator").
 *
 * Real means it reflects an actual probe, not a hard-coded dot. It polls our
 * /health, which aggregates the database and the identity provider — the browser
 * never talks to Sirma directly, since it has no API key and must not be given one.
 *
 * Degraded states are distinguished by label as well as colour, so the indicator
 * does not depend on colour perception alone.
 *
 * ## Why it says nothing when everything is fine
 *
 * "Connected" is the state the console is in essentially always, and a label that
 * is permanently on screen stops being read — so when it eventually says
 * something else, nobody notices. It is silent while healthy and appears only
 * when there is something to say, which is the only condition under which an
 * indicator like this earns its place in the bar.
 *
 * The probe still runs on the same fifteen-second poll: the point is to say
 * nothing, not to know nothing.
 *
 * ## On a phone
 *
 * There is no room for the label, so the pill shrinks to its dot. A coloured dot
 * alone is not an answer, and the hover tooltip that carries the label on a
 * desktop never appears on a touch screen — so there the dot is a button, and
 * tapping it shows the label under the header.
 */
export function ConnectionStatus(): JSX.Element | null {
  const { data, isError, isLoading } = useGetHealthQuery(undefined, {
    pollingInterval: 15_000,
  });

  const { tone, label, detail } = describe({ data, isError, isLoading });
  const compact = useMediaQuery(COMPACT_QUERY);

  // `idle` is the first load, before the first probe has answered. Also silent:
  // a "Checking…" that flashes on every page load is noise about our own
  // startup rather than news about the connection.
  if (tone === 'ok' || tone === 'idle') return null;

  if (compact) return <CompactStatus tone={tone} label={label} />;

  return (
    <div className={`${styles.wrap} ${styles[tone]}`} title={detail}>
      <span className={styles.dot} aria-hidden="true" />
      <span>{label}</span>
      <span className="sr-only">{detail}</span>
    </div>
  );
}

/** The phone version: the dot, which shows the label when tapped. */
function CompactStatus({ tone, label }: { tone: Tone; label: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Closes like the other header popovers: a tap anywhere else, or Escape.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent): void {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={styles.compactWrap} ref={wrapRef}>
      <button
        type="button"
        ref={buttonRef}
        className={`${styles.wrap} ${styles.compact} ${styles[tone]}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={`Connection: ${label}`}
      >
        <span className={styles.dot} aria-hidden="true" />
      </button>

      {open && (
        <div className={`${styles.wrap} ${styles.popover} ${styles[tone]}`} role="status">
          <span className={styles.dot} aria-hidden="true" />
          {label}
        </div>
      )}
    </div>
  );
}

/** Whether a media query matches, kept current as the window is resized. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = (): void => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);

  return matches;
}

type Tone = 'ok' | 'warn' | 'bad' | 'idle';

function describe(state: { data: Health | undefined; isError: boolean; isLoading: boolean }): {
  tone: Tone;
  label: string;
  detail: string;
} {
  if (state.isLoading) return { tone: 'idle', label: 'Checking…', detail: 'Checking connection' };

  // A failed health request is itself the answer: if the API cannot be reached,
  // nothing else on the page is trustworthy either.
  if (state.isError || !state.data) {
    return { tone: 'bad', label: 'Offline', detail: 'Cannot reach the console API' };
  }

  const { status, checks } = state.data;
  if (status === 'down') {
    return { tone: 'bad', label: 'Degraded', detail: `Database ${checks.database}` };
  }
  if (status === 'degraded') {
    return {
      tone: 'warn',
      label: 'Degraded',
      detail: `Identity provider ${checks.idp}, Sirma AI ${checks.sirma}`,
    };
  }
  return {
    tone: 'ok',
    label: 'Connected',
    detail: `Database ok · identity provider ${checks.idp} · Sirma AI ${checks.sirma}`,
  };
}
